import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, FileText, ShieldCheck, PenLine } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const publicApi = axios.create({ baseURL: API });

const TAX_CLASSES = [
  ["individual", "Individual"], ["sole_proprietor", "Sole proprietor / single-member LLC"],
  ["llc", "LLC"], ["s_corp", "S corporation"], ["c_corp", "C corporation"],
  ["partnership", "Partnership"], ["trust_estate", "Trust / estate"], ["other", "Other"],
];

const inputCls = "w-full border border-[#d4d4d8] rounded px-3 py-2.5 text-sm text-[#18181b] focus:outline-none focus:border-[#DC2626] bg-white";
const labelCls = "block text-[10px] uppercase tracking-[0.22em] text-[#71717a] mb-1.5";

export default function DriverOnboarding() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const reload = async () => {
    try {
      const r = await publicApi.get(`/driver-onboarding/${token}`);
      setData(r.data);
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Unable to load onboarding packet");
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, [token]);

  if (err) {
    return (
      <Shell>
        <div className="max-w-md mx-auto text-center mt-24 px-6" data-testid="onboarding-error">
          <h1 className="font-serif text-3xl text-[#DC2626] mb-3">Link invalid</h1>
          <p className="text-sm text-[#52525b]">{err}</p>
          <p className="text-xs text-[#71717a] mt-6">Call dispatch at (414) 333-6275 if you need help.</p>
        </div>
      </Shell>
    );
  }
  if (!data) {
    return <Shell><div className="text-center mt-24 text-sm text-[#52525b]" data-testid="onboarding-loading">Loading your packet…</div></Shell>;
  }

  const allDone = data.w9_completed_at && data.contract_signed_at;

  return (
    <Shell>
      <div className="max-w-3xl mx-auto px-4 md:px-6 pt-8 pb-24" data-testid="onboarding-page">
        <div className="text-center mb-8">
          <div className="text-[10px] tracking-[0.32em] uppercase text-[#DC2626] font-medium">Driver Onboarding Packet</div>
          <h1 className="font-serif text-4xl md:text-5xl text-[#18181b] mt-3 leading-tight">
            {allDone ? "All set!" : `Welcome, ${data.driver_name || "Driver"}`}
          </h1>
          <p className="text-sm text-[#52525b] mt-3 max-w-lg mx-auto">
            {allDone
              ? "Your W-9 and contractor agreement are on file. Dispatch has been updated — you're ready to roll."
              : "Two quick steps: your tax info (W-9) and the contractor agreement. Takes about 5 minutes."}
          </p>
        </div>
        <W9Card token={token} data={data} onDone={reload}/>
        <ContractCard token={token} data={data} onDone={reload}/>
        <My1099s token={token}/>
      </div>
    </Shell>
  );
}

function My1099s({ token }) {
  const [forms, setForms] = useState([]);
  useEffect(() => {
    publicApi.get(`/driver-onboarding/${token}/1099`).then(r => setForms(r.data || [])).catch(() => {});
  }, [token]);
  if (!forms.length) return null;
  return (
    <div className="border border-[#e4e4e7] bg-white rounded p-5 md:p-6 mt-6" data-testid="my-1099s-card">
      <div className="flex items-center gap-2.5 mb-3">
        <FileText size={18} className="text-[#DC2626]"/>
        <div className="text-sm font-semibold text-[#18181b]">Your tax forms (1099-NEC)</div>
      </div>
      <div className="space-y-2">
        {forms.map(f => (
          <a key={f.year} href={`${API}/driver-onboarding/${token}/1099/${f.year}`}
            target="_blank" rel="noopener noreferrer" data-testid={`my-1099-${f.year}`}
            className="flex items-center justify-between border border-[#e4e4e7] rounded px-3 py-2.5 hover:border-[#DC2626] transition-colors">
            <span className="text-sm text-[#18181b] font-medium">{f.year} Form 1099-NEC</span>
            <span className="text-xs text-[#71717a]">${Number(f.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })} · Download PDF</span>
          </a>
        ))}
      </div>
    </div>
  );
}

function StatusChip({ done, doneLabel, pendingLabel }) {
  return done ? (
    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-emerald-700 bg-emerald-50 border border-emerald-300 rounded-full px-2.5 py-1">
      <CheckCircle2 size={11}/> {doneLabel}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-amber-700 bg-amber-50 border border-amber-300 rounded-full px-2.5 py-1">
      ● {pendingLabel}
    </span>
  );
}

function W9Card({ token, data, onDone }) {
  const done = !!data.w9_completed_at;
  const [open, setOpen] = useState(!done);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    legal_name: data.prefill?.name || "", business_name: "",
    tax_classification: "individual", address: "", city: "", state: "WI", zip: "",
    tin_type: "ssn", tin: "", signature_name: "", certify: false,
  });
  const upd = (k, v) => setF(p => ({ ...p, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      await publicApi.post(`/driver-onboarding/${token}/w9`, f);
      toast.success("W-9 submitted — thank you!");
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit W-9");
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-[#e4e4e7] bg-white rounded p-5 md:p-6 mb-6" data-testid="w9-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <FileText size={18} className="text-[#DC2626]"/>
          <div>
            <div className="text-sm font-semibold text-[#18181b]">Step 1 · Tax info (Form W-9)</div>
            <div className="text-xs text-[#71717a]">Needed for your year-end 1099. Your SSN/EIN is stored encrypted.</div>
          </div>
        </div>
        <StatusChip done={done} doneLabel="On file" pendingLabel="Needed"/>
      </div>

      {done && !open && (
        <div className="mt-4 flex items-center justify-between gap-3 border border-emerald-200 bg-emerald-50/60 rounded p-3">
          <div className="text-xs text-emerald-800" data-testid="w9-done-summary">
            {data.w9_summary?.legal_name} · {(data.w9_summary?.tin_type || "").toUpperCase()} ending in {data.w9_summary?.tin_last4}
            {" "}· completed {new Date(data.w9_completed_at).toLocaleDateString()}
          </div>
          <button data-testid="w9-redo-btn" onClick={() => setOpen(true)}
            className="text-[10px] uppercase tracking-[0.18em] text-[#71717a] hover:text-[#DC2626] whitespace-nowrap">Update</button>
        </div>
      )}

      {open && (
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="w9-form">
          <div><label className={labelCls}>Legal name (as on tax return) *</label>
            <input data-testid="w9-legal-name" className={inputCls} value={f.legal_name} onChange={e => upd("legal_name", e.target.value)}/></div>
          <div><label className={labelCls}>Business name (if different)</label>
            <input data-testid="w9-business-name" className={inputCls} value={f.business_name} onChange={e => upd("business_name", e.target.value)}/></div>
          <div><label className={labelCls}>Federal tax classification *</label>
            <select data-testid="w9-tax-class" className={inputCls} value={f.tax_classification} onChange={e => upd("tax_classification", e.target.value)}>
              {TAX_CLASSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>ID type *</label>
              <select data-testid="w9-tin-type" className={inputCls} value={f.tin_type} onChange={e => upd("tin_type", e.target.value)}>
                <option value="ssn">SSN</option><option value="ein">EIN</option>
              </select></div>
            <div><label className={labelCls}>{f.tin_type === "ein" ? "EIN" : "SSN"} (9 digits) *</label>
              <input data-testid="w9-tin" className={inputCls} inputMode="numeric" placeholder={f.tin_type === "ein" ? "12-3456789" : "123-45-6789"}
                value={f.tin} onChange={e => upd("tin", e.target.value)}/></div>
          </div>
          <div className="sm:col-span-2"><label className={labelCls}>Street address *</label>
            <input data-testid="w9-address" className={inputCls} value={f.address} onChange={e => upd("address", e.target.value)}/></div>
          <div><label className={labelCls}>City *</label>
            <input data-testid="w9-city" className={inputCls} value={f.city} onChange={e => upd("city", e.target.value)}/></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>State *</label>
              <input data-testid="w9-state" className={inputCls} maxLength={2} value={f.state} onChange={e => upd("state", e.target.value.toUpperCase())}/></div>
            <div><label className={labelCls}>ZIP *</label>
              <input data-testid="w9-zip" className={inputCls} inputMode="numeric" value={f.zip} onChange={e => upd("zip", e.target.value)}/></div>
          </div>
          <div className="sm:col-span-2 border-t border-[#f1f1f3] pt-4">
            <div className="text-[11px] text-[#52525b] leading-relaxed mb-3">
              Under penalties of perjury, I certify that the number shown on this form is my correct
              taxpayer identification number, I am not subject to backup withholding, and I am a U.S.
              citizen or other U.S. person.
            </div>
            <label className="flex items-start gap-2.5 text-sm text-[#18181b] mb-4">
              <input type="checkbox" data-testid="w9-certify" checked={f.certify} onChange={e => upd("certify", e.target.checked)} className="mt-0.5"/>
              I certify the above information is true and correct.
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
              <div><label className={labelCls}>Type your full name as signature *</label>
                <input data-testid="w9-signature" className={inputCls} placeholder="Your full legal name" value={f.signature_name} onChange={e => upd("signature_name", e.target.value)}/></div>
              <button data-testid="w9-submit" onClick={submit} disabled={busy || !f.certify}
                className="w-full py-3 bg-[#18181b] text-white text-xs font-bold uppercase tracking-[0.22em] rounded hover:bg-[#DC2626] disabled:opacity-40 transition-colors">
                {busy ? "Submitting…" : "Submit W-9"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ContractCard({ token, data, onDone }) {
  const done = !!data.contract_signed_at;
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);

  const sign = async () => {
    setBusy(true);
    try {
      await publicApi.post(`/driver-onboarding/${token}/contract`, { signature_name: name, agree });
      toast.success("Contract signed — welcome aboard!");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not sign contract");
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-[#e4e4e7] bg-white rounded p-5 md:p-6" data-testid="contract-card">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2.5">
          <ShieldCheck size={18} className="text-[#DC2626]"/>
          <div>
            <div className="text-sm font-semibold text-[#18181b]">Step 2 · Independent Contractor Agreement</div>
            <div className="text-xs text-[#71717a]">Please read carefully, then sign by typing your name.</div>
          </div>
        </div>
        <StatusChip done={done} doneLabel="Signed" pendingLabel="Needs signature"/>
      </div>

      <div data-testid="contract-text"
        className="border border-[#e4e4e7] bg-[#fafafa] rounded p-4 max-h-80 overflow-y-auto text-[12px] leading-relaxed text-[#3f3f46] whitespace-pre-wrap">
        {data.contract_text}
      </div>

      {done ? (
        <div className="mt-4 border border-emerald-200 bg-emerald-50/60 rounded p-3 text-xs text-emerald-800" data-testid="contract-done">
          <CheckCircle2 size={13} className="inline mr-1.5 -mt-0.5"/>
          Signed by <b>{data.contract_signed_name}</b> on {new Date(data.contract_signed_at).toLocaleString()}
        </div>
      ) : (
        <div className="mt-5">
          <label className="flex items-start gap-2.5 text-sm text-[#18181b] mb-4">
            <input type="checkbox" data-testid="contract-agree" checked={agree} onChange={e => setAgree(e.target.checked)} className="mt-0.5"/>
            I have read and agree to the Independent Contractor Agreement above.
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
            <div>
              <label className={labelCls}><PenLine size={10} className="inline mr-1 -mt-0.5"/>Type your full name to sign *</label>
              <input data-testid="contract-signature" className={inputCls} placeholder="Your full legal name"
                value={name} onChange={e => setName(e.target.value)}/>
            </div>
            <button data-testid="contract-sign-btn" onClick={sign} disabled={busy || !agree || name.trim().length < 3}
              className="w-full py-3 bg-[#DC2626] text-white text-xs font-bold uppercase tracking-[0.22em] rounded hover:bg-[#B91C1C] disabled:opacity-40 transition-colors">
              {busy ? "Signing…" : "Sign Agreement"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-white text-[#18181b]" data-testid="onboarding-shell">
      <header className="border-b border-[#e4e4e7] py-5 px-5 md:px-8 bg-white">
        <div className="flex items-center justify-center gap-4 md:gap-5">
          <img src="/logos/monogram.png" alt="Delux Limo Trans Group"
            className="w-14 h-14 md:w-16 md:h-16 object-contain flex-shrink-0"/>
          <div className="text-center">
            <div className="font-serif text-xl md:text-3xl text-[#DC2626] tracking-[0.12em] leading-tight">Delux Limo Trans Group</div>
            <div className="text-[10px] md:text-[11px] text-[#52525b] tracking-[0.18em] uppercase mt-1">Premium Chauffeur &amp; Luxury Transportation</div>
            <div className="text-[10px] text-[#DC2626] tracking-[0.32em] uppercase mt-2 font-medium">Driver Onboarding</div>
          </div>
        </div>
      </header>
      {children}
      <footer className="border-t border-[#e4e4e7] py-6 text-center text-[10px] text-[#71717a] tracking-[0.18em] uppercase bg-white">
        Dispatch · (414) 333-6275 · deluxlimotransgroup.com
      </footer>
    </div>
  );
}
